import { useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { keys, useTasks } from '../lib/queries'
import { filterQuery, parseFilters, type Filters } from '../lib/filters'
import { isOverdue, parseDueDate } from '../lib/format'
import type { Task, User } from '../lib/types'
import { TaskList } from '../components/TaskList'
import { TaskDrawer } from '../components/TaskDrawer'
import { Button, EmptyState, Spinner, cx } from '../components/ui'

/**
 * My Tasks — everything assigned to you across every project, grouped by
 * urgency rather than by board. For the personal half of this tool it is the
 * screen that actually gets opened each morning.
 */
export function MyTasksPage({ user }: { user: User }) {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()

  const openTaskId = params.get('task')
  const scope = params.get('scope') === 'all' ? 'all' : 'mine'

  const filters: Filters = useMemo(() => {
    const parsed = parseFilters(params)
    return scope === 'mine' ? { ...parsed, assignees: [user.id] } : parsed
  }, [params, scope, user.id])

  const query = useMemo(() => filterQuery(filters), [filters])
  const tasks = useTasks(query)

  // The drawer needs statuses and members for its pickers, and those belong to
  // whichever project the opened task lives in.
  const open = useQuery({
    queryKey: keys.task(openTaskId ?? ''),
    queryFn: () => api.task(openTaskId!),
    enabled: Boolean(openTaskId),
  })
  const statuses = useQuery({
    queryKey: keys.statuses(open.data?.projectId ?? ''),
    queryFn: () => api.statuses(open.data!.projectId),
    enabled: Boolean(open.data?.projectId),
  })
  const members = useQuery({
    queryKey: keys.members(open.data?.projectId ?? ''),
    queryFn: () => api.members(open.data!.projectId),
    enabled: Boolean(open.data?.projectId),
  })

  const openTask = useCallback(
    (task: Task) => {
      const next = new URLSearchParams(params)
      next.set('task', task.id)
      setParams(next)
    },
    [params, setParams],
  )

  const closeDrawer = useCallback(() => {
    const next = new URLSearchParams(params)
    next.delete('task')
    setParams(next, { replace: true })
  }, [params, setParams])

  function setScope(next: 'mine' | 'all') {
    const p = new URLSearchParams(params)
    if (next === 'all') p.set('scope', 'all')
    else p.delete('scope')
    setParams(p, { replace: true })
  }

  // Sorted so the things that need a decision today are at the top, then
  // grouped by the same buckets — the order carries the meaning, the headings
  // just name it.
  const sorted = useMemo(() => {
    const list = [...(tasks.data ?? [])]
    list.sort((a, b) => bucketRank(a) - bucketRank(b) || dueValue(a) - dueValue(b))
    return list
  }, [tasks.data])

  return (
    <>
      <header className="flex items-center gap-2.5 px-4 py-3">
        <h1 className="text-[15px] font-semibold tracking-tight">My tasks</h1>
        {tasks.data && (
          <span className="task-key text-[12px] text-[var(--ink-3)]">{tasks.data.length}</span>
        )}

        <div className="ml-auto flex rounded-md border border-[var(--line)] p-0.5">
          <ScopeTab active={scope === 'mine'} onClick={() => setScope('mine')}>
            Assigned to me
          </ScopeTab>
          <ScopeTab active={scope === 'all'} onClick={() => setScope('all')}>
            Everything
          </ScopeTab>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto border-t border-[var(--line)]">
        {tasks.isLoading && !tasks.data ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner className="h-5 w-5 text-[var(--ink-3)]" />
          </div>
        ) : sorted.length === 0 ? (
          <EmptyState
            title={scope === 'mine' ? 'Nothing is assigned to you' : 'No tasks yet'}
            hint={
              scope === 'mine'
                ? 'Tasks assigned to you across every board show up here.'
                : 'Open a board and add the first one.'
            }
            action={
              <Button onClick={() => navigate('/')}>Go to a board</Button>
            }
          />
        ) : (
          <TaskList tasks={sorted} onOpen={openTask} groupBy={bucketLabel} />
        )}
      </div>

      {openTaskId && open.data && (
        <TaskDrawer
          taskId={open.data.id}
          statuses={statuses.data ?? []}
          members={members.data ?? []}
          user={user}
          onClose={closeDrawer}
        />
      )}
    </>
  )
}

function ScopeTab({
  active, onClick, children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        'rounded px-2 py-0.5 text-[12px] transition-colors duration-[140ms]',
        active
          ? 'bg-[var(--sunken)] font-medium text-[var(--ink)]'
          : 'text-[var(--ink-3)] hover:text-[var(--ink-2)]',
      )}
    >
      {children}
    </button>
  )
}

function bucketRank(task: Task): number {
  if (task.statusDone) return 5
  if (isOverdue(task)) return 0
  const due = parseDueDate(task.dueDate)
  if (!due) return 4
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000)
  if (days === 0) return 1
  if (days <= 7) return 2
  return 3
}

function bucketLabel(task: Task): string {
  return ['Overdue', 'Due today', 'This week', 'Later', 'No due date', 'Done'][bucketRank(task)]!
}

function dueValue(task: Task): number {
  return parseDueDate(task.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER
}
